/**
 * Sound effects hook using Web Audio API
 * Generates pleasant game sounds programmatically
 */

import { useCallback, useEffect, useRef } from 'react';
import { useSettingsStore } from '../stores/settingsStore';

type SoundType = 'yourTurn' | 'wordClaimed' | 'wordStolen' | 'tileFlip' | 'error';

// Audio context singleton (created on first user interaction)
let audioContext: AudioContext | null = null;

// iOS requires user gesture to unlock audio
let audioUnlocked = false;

function getAudioContext(): AudioContext | null {
  if (!audioContext && typeof window !== 'undefined') {
    try {
      audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch (e) {
      console.warn('Web Audio API not supported');
      return null;
    }
  }
  return audioContext;
}

// Unlock audio on iOS (call this on first user interaction)
export function unlockAudio(): void {
  if (audioUnlocked) return;

  const ctx = getAudioContext();
  if (!ctx) return;

  // Create and play a silent buffer to unlock
  const buffer = ctx.createBuffer(1, 1, 22050);
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.connect(ctx.destination);
  source.start(0);

  // Resume context if suspended (iOS Safari)
  if (ctx.state === 'suspended') {
    ctx.resume();
  }

  audioUnlocked = true;
}

/**
 * Creates a gentle rising chime sound for "your turn"
 * Musical: Uses major third interval for pleasant feeling
 */
function playYourTurnSound(ctx: AudioContext): void {
  const now = ctx.currentTime;

  // Primary note (C5)
  const osc1 = ctx.createOscillator();
  const gain1 = ctx.createGain();
  osc1.type = 'sine';
  osc1.frequency.setValueAtTime(523.25, now); // C5
  gain1.gain.setValueAtTime(0, now);
  gain1.gain.linearRampToValueAtTime(0.3, now + 0.05);
  gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.4);
  osc1.connect(gain1);
  gain1.connect(ctx.destination);
  osc1.start(now);
  osc1.stop(now + 0.4);

  // Second note (E5) - major third above, slightly delayed
  const osc2 = ctx.createOscillator();
  const gain2 = ctx.createGain();
  osc2.type = 'sine';
  osc2.frequency.setValueAtTime(659.25, now + 0.1); // E5
  gain2.gain.setValueAtTime(0, now + 0.1);
  gain2.gain.linearRampToValueAtTime(0.25, now + 0.15);
  gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
  osc2.connect(gain2);
  gain2.connect(ctx.destination);
  osc2.start(now + 0.1);
  osc2.stop(now + 0.5);

  // Third note (G5) - fifth, completing major chord
  const osc3 = ctx.createOscillator();
  const gain3 = ctx.createGain();
  osc3.type = 'sine';
  osc3.frequency.setValueAtTime(783.99, now + 0.2); // G5
  gain3.gain.setValueAtTime(0, now + 0.2);
  gain3.gain.linearRampToValueAtTime(0.2, now + 0.25);
  gain3.gain.exponentialRampToValueAtTime(0.01, now + 0.6);
  osc3.connect(gain3);
  gain3.connect(ctx.destination);
  osc3.start(now + 0.2);
  osc3.stop(now + 0.6);
}

/**
 * Creates a bright positive "ding" for word claimed
 * Single high note with harmonics
 */
function playWordClaimedSound(ctx: AudioContext): void {
  const now = ctx.currentTime;

  // Fundamental (A5)
  const osc1 = ctx.createOscillator();
  const gain1 = ctx.createGain();
  osc1.type = 'sine';
  osc1.frequency.setValueAtTime(880, now); // A5
  gain1.gain.setValueAtTime(0, now);
  gain1.gain.linearRampToValueAtTime(0.35, now + 0.02);
  gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
  osc1.connect(gain1);
  gain1.connect(ctx.destination);
  osc1.start(now);
  osc1.stop(now + 0.3);

  // Add harmonic for brightness
  const osc2 = ctx.createOscillator();
  const gain2 = ctx.createGain();
  osc2.type = 'sine';
  osc2.frequency.setValueAtTime(1760, now); // A6 (octave)
  gain2.gain.setValueAtTime(0, now);
  gain2.gain.linearRampToValueAtTime(0.15, now + 0.02);
  gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
  osc2.connect(gain2);
  gain2.connect(ctx.destination);
  osc2.start(now);
  osc2.stop(now + 0.2);
}

/**
 * Creates a descending "whoosh" for word stolen
 * Minor interval for slightly tense but not harsh feeling
 */
function playWordStolenSound(ctx: AudioContext): void {
  const now = ctx.currentTime;

  // Descending slide
  const osc1 = ctx.createOscillator();
  const gain1 = ctx.createGain();
  osc1.type = 'triangle';
  osc1.frequency.setValueAtTime(600, now);
  osc1.frequency.exponentialRampToValueAtTime(200, now + 0.3);
  gain1.gain.setValueAtTime(0, now);
  gain1.gain.linearRampToValueAtTime(0.25, now + 0.03);
  gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.35);
  osc1.connect(gain1);
  gain1.connect(ctx.destination);
  osc1.start(now);
  osc1.stop(now + 0.35);

  // Add noise burst for "swoosh" texture
  const bufferSize = ctx.sampleRate * 0.15;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
  }

  const noise = ctx.createBufferSource();
  const noiseGain = ctx.createGain();
  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(800, now);
  filter.Q.setValueAtTime(1, now);

  noise.buffer = buffer;
  noise.connect(filter);
  filter.connect(noiseGain);
  noiseGain.gain.setValueAtTime(0.08, now);
  noiseGain.connect(ctx.destination);
  noise.start(now);
}

/**
 * Creates a soft click for tile flip
 */
function playTileFlipSound(ctx: AudioContext): void {
  const now = ctx.currentTime;

  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(1200, now);
  osc.frequency.exponentialRampToValueAtTime(400, now + 0.05);
  gain.gain.setValueAtTime(0.15, now);
  gain.gain.exponentialRampToValueAtTime(0.01, now + 0.08);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.08);
}

/**
 * Creates a soft "buzz" for error/rejection
 */
function playErrorSound(ctx: AudioContext): void {
  const now = ctx.currentTime;

  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'square';
  osc.frequency.setValueAtTime(150, now);
  gain.gain.setValueAtTime(0.1, now);
  gain.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.15);
}

/**
 * Hook to play game sound effects
 * Respects user's sound settings
 */
export function useSoundEffects() {
  const soundEnabled = useSettingsStore((s) => s.soundEffects);
  const lastPlayedRef = useRef<{ [key: string]: number }>({});

  // Unlock audio on first user interaction
  useEffect(() => {
    const unlock = () => {
      unlockAudio();
      // Remove listeners after first interaction
      window.removeEventListener('click', unlock);
      window.removeEventListener('touchstart', unlock);
      window.removeEventListener('keydown', unlock);
    };

    window.addEventListener('click', unlock);
    window.addEventListener('touchstart', unlock);
    window.addEventListener('keydown', unlock);

    return () => {
      window.removeEventListener('click', unlock);
      window.removeEventListener('touchstart', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  const playSound = useCallback((type: SoundType) => {
    if (!soundEnabled) return;

    const ctx = getAudioContext();
    if (!ctx) return;

    // Resume context if suspended
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    // Debounce rapid sounds (prevent overlap)
    const now = Date.now();
    const lastPlayed = lastPlayedRef.current[type] || 0;
    if (now - lastPlayed < 100) return;
    lastPlayedRef.current[type] = now;

    switch (type) {
      case 'yourTurn':
        playYourTurnSound(ctx);
        break;
      case 'wordClaimed':
        playWordClaimedSound(ctx);
        break;
      case 'wordStolen':
        playWordStolenSound(ctx);
        break;
      case 'tileFlip':
        playTileFlipSound(ctx);
        break;
      case 'error':
        playErrorSound(ctx);
        break;
    }
  }, [soundEnabled]);

  return { playSound };
}
